import { RevealLines } from "@/components/RevealText";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Marquee } from "@/components/Marquee";
import { SERVICES, PROCESS } from "@/data/site";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1771970472779-6452fbce94d6?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

const Services = () => (
  <div data-testid="services-page">
    <section className="pt-40 pb-16 border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Breadcrumbs items={[{ label: "Services", to: "/services" }]} />
        <div className="mt-10 flex items-center gap-4">
          <div className="h-px w-12 bg-edge" />
          <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">
            Services
          </span>
        </div>
        <RevealLines
          as="h1"
          lines={[
            "Supply. Install.",
            <>
              Then <span className="text-edge">stand behind</span> it.
            </>,
          ]}
          className="mt-8 font-display font-black uppercase tracking-tighter text-[12vw] md:text-[9vw] lg:text-[6.5vw] leading-[0.9]"
        />
      </div>
    </section>

    <section className="border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.num}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: i * 0.05 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-12 gap-8 py-16 border-b border-white/10 items-start"
            data-testid={`service-${s.num}`}
          >
            <div className="lg:col-span-1 font-editorial italic text-edge text-5xl">
              {s.num}
            </div>
            <div className="lg:col-span-5">
              <h2 className="font-display uppercase text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">
                {s.title}
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 text-white/75 text-lg leading-relaxed">
              {s.blurb}
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    <Marquee
      items={[
        "Free Site Inspection",
        "48-Hour Quote",
        "Tape-Sealed Joints",
        "Monsoon Follow-Up",
      ]}
    />

    <section className="py-24 border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative overflow-hidden aspect-[4/3]">
          <img
            src={IMG}
            alt="Industrial ceiling"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-ink-950/70 to-transparent" />
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">
            Working Process
          </div>
          <h2 className="font-display uppercase text-4xl md:text-5xl font-black tracking-tighter">
            How a project moves.
          </h2>
          <div className="mt-10 space-y-6">
            {PROCESS.map((p) => (
              <div
                key={p.num}
                className="flex items-start gap-4 border-t border-white/10 pt-4"
              >
                <div className="font-editorial italic text-edge text-2xl w-10">
                  {p.num}
                </div>
                <div>
                  <div className="font-display uppercase font-bold tracking-tight">
                    {p.title}
                  </div>
                  <div className="text-white/65 mt-1 text-sm">{p.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-24 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <h2 className="font-display uppercase text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
            Ready to schedule your free inspection?
          </h2>
          <ul className="mt-8 space-y-3 text-white/80">
            {[
              "No fee, no pressure",
              "Engineer on site within 48 hours",
              "Written proposal within 48 hours of inspection",
              "Full material certifications shared upfront",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-edge" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-ink-900/50 p-8 lg:p-10">
          <EnquiryForm variant="full" source="services-page" />
        </div>
      </div>
    </section>
  </div>
);

export default Services;
